import { Stack } from '@mui/material';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { DialogPromptImage } from '../../api/DialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import ChatBubble from '../ChatBubble';
import { getAudioByText } from './DialogPromptComponent';

interface DialogPromptImageComponentProps {
    dialogPrompt: DialogPromptImage;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

const DialogPromptImageComponent: React.FC<DialogPromptImageComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();

    const { clearFooterList } = React.useContext(DialogPromptFooterContext)

    const imageRef = React.useRef<HTMLImageElement>(null);

    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);

    const [imageSize, setImageSize] = React.useState<{ width: number; height: number } | null>(null);
    const [showLightbox, setShowLightbox] = React.useState(false);
    const [hovering, setHovering] = React.useState(false);

    const handleImageLoad = () => {
        if (imageRef.current) {
            setImageSize({
                width: imageRef.current.naturalWidth,
                height: imageRef.current.naturalHeight
            });
        }
    };

    React.useEffect(() => {
        clearFooterList()

        if (dialogPrompt.prompt == null || dialogPrompt.prompt === "") {
            setChatBubbleDone(true);
        } else {
            getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        }
    }, [clearFooterList, dialogPrompt.prompt, history, setErrorMessage]);

    React.useEffect(() => {
        if (chatBubbleDone) {
            if ((dialogPrompt.image_src && dialogPrompt.image_src != "broken") || (dialogPrompt.image_src_url && dialogPrompt.image_src != "broken")) {
                // make sure to scroll after the image is loaded.
                const handleScrollAfterLoad = () => {
                    if (chatBubbleContainerRef.current) {
                        scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
                    }
                    setDialogPromptComplete(dialogPrompt.id)
                };

                if (imageRef.current) {
                    if (imageRef.current.complete && imageRef.current.naturalHeight !== 0) {
                        // If image is already loaded
                        handleImageLoad();
                        handleScrollAfterLoad();
                    } else {
                        // Image is not yet loaded, attach event listener
                        imageRef.current.addEventListener('load', () => {
                            handleImageLoad();
                            handleScrollAfterLoad();
                        });
                    }
                }

                const imageEl = imageRef.current;
                return () => {
                    if (imageEl) {
                        imageEl.removeEventListener('load', handleImageLoad);
                    }
                };
            } else {
                setDialogPromptComplete(dialogPrompt.id)
            }
        }
    }, [chatBubbleDone, chatBubbleContainerRef, dialogPrompt.id, dialogPrompt.image_src, dialogPrompt.image_src_url, setDialogPromptComplete]);

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {chatBubbleDone && (
                <div style={{ position: 'relative', display: 'inline-block', margin: '0 auto' }}>
                    <img
                        ref={imageRef}
                        src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : "broken")}
                        alt={dialogPrompt.prompt}
                        onClick={() => setShowLightbox(true)}
                        onMouseEnter={() => setHovering(true)}
                        onMouseLeave={() => setHovering(false)}
                        style={{
                            maxHeight: '400px',
                            maxWidth: "100%",
                            height: 'auto', width: 'auto',
                            display: 'block',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s ease',
                            opacity: hovering ? 0.85 : 1,
                            borderRadius: '10px',
                        }}
                    />

                    {hovering && imageSize && (
                        <div style={{
                            position: 'absolute',
                            bottom: '8px',
                            right: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: '#fff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            pointerEvents: 'none',
                            zIndex: 10,
                        }}>
                            {imageSize.width} × {imageSize.height}
                        </div>
                    )}
                </div>
            )}

            {/* Lightbox */}
            {showLightbox && (
                <div
                    onClick={() => setShowLightbox(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 9999,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()} // prevent closing when clicking content
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '6px',
                            cursor: 'default',
                        }}
                    >
                        <img
                            src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : "broken")}
                            alt={dialogPrompt.prompt}
                            onLoad={(e) => {
                                const img = e.currentTarget;
                                setImageSize({
                                    width: img.naturalWidth,
                                    height: img.naturalHeight,
                                });
                            }}
                            style={{
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                borderRadius: '10px',
                                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)',
                            }}
                        />

                        {imageSize && (
                            <div
                                style={{
                                    color: '#ccc',
                                    fontSize: '12px',
                                    paddingRight: '4px',
                                }}
                            >
                                {imageSize.width} × {imageSize.height}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Stack>
    );
};

export default DialogPromptImageComponent;

